import Button from '@rio-cloud/rio-uikit/Button';
import { FunctionComponent } from 'react';
import { assertExhaustive } from '../../../../../utils';
import { NoValue } from '../../../sharedComponents/NoValue';
import { DateTime } from '../../../sharedComponents/table/BaseTableComponents';
import { AllowedAction, OutgoingVehicle } from '../../compoundManagement.types';
import {
    DisplayDepartureFreightForwarder,
    DisplayDepartureToCity,
    DisplayDepartureToLocationId,
} from '../../shared/dataDisplay/DepartureDataContent';
import { DisplayCancellableData } from '../../shared/dataDisplay/DisplayCancellableData';
import { DisplayDispatchType } from '../../shared/dataDisplay/DisplayDispatchType';
import { DisplayFuelType } from '../../shared/dataDisplay/DisplayFuelType';
import {
    DisplayMeansOfTransportId,
    DisplayMeansOfTransportType,
} from '../../shared/dataDisplay/DisplayMeansOfTransportType';
import { DisplayModelName } from '../../shared/dataDisplay/DisplayModelName';
import { DisplaySpecialInstructions } from '../../shared/dataDisplay/DisplaySpecialInstructions';
import { CompoundVehicleActionButton } from '../../shared/table/CompoundVehicleActionButton';
import { OutgoingVehiclesTableColumn } from './OutgoingVehiclesTableColumn';

type TableDataContentProps = { column: OutgoingVehiclesTableColumn; vehicle: OutgoingVehicle };

export const OutgoingVehiclesTableDataContent: FunctionComponent<TableDataContentProps> = ({ column, vehicle }) => {
    const { isCancelled } = vehicle.departure;
    const allowsDeparture = vehicle.allowedActions.includes(AllowedAction.REPORT_ANNOUNCED_DEPARTURE);

    switch (column) {
        case OutgoingVehiclesTableColumn.VIN:
            return <div className="ellipsis-1">{vehicle.vehicle.vin}</div>;
        case OutgoingVehiclesTableColumn.LOCATION_ID:
            return <span>{vehicle.compoundLocation.identifier}</span>;
        case OutgoingVehiclesTableColumn.LOCATION_CITY:
            return <span>{vehicle.compoundLocation.address.city}</span>;
        case OutgoingVehiclesTableColumn.EXPECTED_DEPARTURE_DATE:
            return (
                <DisplayCancellableData isCancelled={isCancelled}>
                    <DateTime
                        dateTime={vehicle.departure.expectedDepartureAt}
                        timeZone={vehicle.compoundLocation.timeZone}
                    />
                </DisplayCancellableData>
            );
        case OutgoingVehiclesTableColumn.DEPARTURE_TO_LOCATION_ID:
            return <DisplayDepartureToLocationId compoundVehicle={vehicle} />;
        case OutgoingVehiclesTableColumn.DEPARTURE_TO_CITY:
            return <DisplayDepartureToCity compoundVehicle={vehicle} />;
        case OutgoingVehiclesTableColumn.MANUFACTURER:
            return vehicle.vehicle.model.manufacturer ?? <NoValue />;
        case OutgoingVehiclesTableColumn.MODEL:
            return <DisplayModelName vehicle={vehicle.vehicle} />;
        case OutgoingVehiclesTableColumn.DISPATCH_TYPE:
            return <DisplayDispatchType vehicle={vehicle.vehicle} />;
        case OutgoingVehiclesTableColumn.FUEL_TYPE:
            return <DisplayFuelType vehicle={vehicle.vehicle} />;
        case OutgoingVehiclesTableColumn.MODEL_VERSION:
            return vehicle.vehicle.model.version ?? <NoValue />;
        case OutgoingVehiclesTableColumn.SPECIAL_INSTRUCTIONS:
            return <DisplaySpecialInstructions specialInstructions={vehicle.vehicle.specialInstructions} />;
        case OutgoingVehiclesTableColumn.DEPARTURE_FREIGHT_FORWARDER:
            return <DisplayDepartureFreightForwarder departure={vehicle.departure} />;
        case OutgoingVehiclesTableColumn.MEANS_OF_TRANSPORT:
            return (
                <DisplayMeansOfTransportId
                    meansOfTransportId={vehicle.departure.transportInformation.meansOfTransportId}
                    isCancelled={isCancelled}
                />
            );
        case OutgoingVehiclesTableColumn.MEANS_OF_TRANSPORT_TYPE:
            return (
                <DisplayCancellableData isCancelled={isCancelled}>
                    <DisplayMeansOfTransportType
                        meansOfTransportType={vehicle.departure.transportInformation.meansOfTransportType}
                    />
                </DisplayCancellableData>
            );
        case OutgoingVehiclesTableColumn.ACTION:
            return (
                allowsDeparture && (
                    <CompoundVehicleActionButton
                        labelId="outboundPortal.compoundManagement.button.confirmDeparture"
                        icon="rioglyph-load-unload"
                        vehicle={vehicle}
                        buttonStyle={Button.PRIMARY}
                    />
                )
            );
        default:
            assertExhaustive(column);
    }
};
